import { useState, useEffect, useRef } from 'react';
import { FaEye, FaTrash } from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import useHttpApiCall from '../../../services/useHttpApiCall';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip, Modal, Button, Alert } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { markDashboardUpdated } from '../../../redux/myDashboard/dashboardSlice';
import { AiOutlineInfoCircle } from 'react-icons/ai';

export default function MyDashboardList({ setOpenDashboardListSlider, setOpenSelectGraphSlider }) {
  const [dashboards, setDashboards] = useState([]);
  const [payload, setPayload] = useState(undefined);
  const [deletePayload, setDeletePayload] = useState(undefined);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [data, loading, error] = useHttpApiCall(payload);
  const [deleteData, deleteLoading, deleteError] = useHttpApiCall(deletePayload);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [activeDbCount, setActiveDbCount] = useState(null);
  const [showLimitMessage, setShowLimitMessage] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const modalRef = useRef(null);
  //console.log("Dashboard",dashboards);

  //api call
  useEffect(() => {
    setPayload({
      method: 'GET',
      auth: true,
      url: 'v0/my-dashboard/'
    });
  }, []);

  useEffect(() => {
    if (data?.data?.data) {
      setDashboards(data.data.data);
    }
    if (error?.response?.status === 401) {
      setAuthError(true);
    }
    if (data?.data) {
      setActiveDbCount(data.data.user_dashboard_limit);
    }
  }, [data, error]);
useEffect(() => {
  if (activeDbCount !== null) {
    if (dashboards.length >= activeDbCount) {
      setShowLimitMessage(true);   // turn on when limit reached
    } else {
      setShowLimitMessage(false);  // turn off when below limit
    }
  }
}, [dashboards.length, activeDbCount]);
  //handle outside

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If click is outside BOTH sidebar and modal, close the sidebar
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        (!modalRef.current || !modalRef.current.contains(event.target))
      ) {
        setOpenDashboardListSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setOpenDashboardListSlider]); // Handle delete responses
  useEffect(() => {
    if (deleteData) {
      setDashboards((prev) => prev.filter((d) => d.id !== deleteId));

      setDeletePayload(undefined);

      setAlertMessage('Deleted successfully');
      setAlertVariant('success');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);
    }
    if (deleteError) {
      setAlertMessage(deleteError.response?.data?.message || 'Failed to delete dashboard');
      setAlertVariant('danger');
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 3000);

      // Reset payload on error also
      setDeletePayload(undefined);
    }
  }, [deleteData, deleteError]);

  const handleClose = () => {
    setOpenDashboardListSlider(false);
  };

  const handleEdit = (id) => {
    navigate(`/viewDashboardList/${id}`);
    setOpenSelectGraphSlider(false);
    setOpenDashboardListSlider(false);
    dispatch(markDashboardUpdated()); // notify sidebar
  };

  const confirmDelete = (id) => {
    //console.log("cdeleteId", id)
    setDeleteId(id);
    setShowDeleteModal(true);
    setOpenDashboardListSlider(true);
  };

  const performDelete = () => {
    if (!deleteId) return;
    setDeletePayload({
      method: 'DELETE',
      auth: true,
      url: `v0/my-dashboard/${deleteId}/`
    });
    dispatch(markDashboardUpdated()); //notify sidebar
    setShowDeleteModal(false);
  };

  const handleView = (id) => {
    navigate(`/myDashboard/view/${id}`);
    setOpenDashboardListSlider(false);
  };

  return (
    <div className="select-sensor-classification">
      <div className="sidebar-slider select-graph" ref={sidebarRef}>
        <i className="sidebar-slider_close material-icons" onClick={handleClose}>
          cancel
        </i>

        <div className="select-source">
          <h3 className="text-center py-4">Saved Dashboards</h3>
            {showLimitMessage && ( 
          <div className="d-flex mb-4">
            <div className="bg-light">
              <AiOutlineInfoCircle
                className="text-primary me-1"
                size={14} // keep icon same height as small text
              />
          <small className="text-muted">
               Dashboard limit {activeDbCount} reached. To create a new one, please delete an existing dashboard or contact support@dazoq.com to increase your limit.
              </small>
            </div>
          </div>
          )}
          {showAlert && (
            <Alert
              variant={alertVariant}
              dismissible
              onClose={() => setShowAlert(false)}
              className="mx-3">
              {alertMessage}
            </Alert>
          )}

          {loading && (
            <div className="text-center py-5">
              <p>Loading...</p>
            </div>
          )}

          {authError && <div className="text-center py-5 ">Loading...</div>}

          {!loading && !authError && (
            <div className="saved-dashboards-list">
              {dashboards.length > 0 ? (
                dashboards.map((dashboard, index) => {
                  const isDisabled = !dashboard?.is_active;
                  //console.log("isDisabled",isDisabled)

                  return (
                    <div key={dashboard.id}>
                      <div
                        className={`d-flex justify-content-between align-items-center mb-2 ${
                          isDisabled ? 'opacity-50' : ''
                        }`}>
                        <OverlayTrigger
                          placement="bottom"
                          overlay={
                            <Tooltip id={`tooltip-view-${dashboard.id}`}>{dashboard.name}</Tooltip>
                          }>
                          <span
                            style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                            onClick={() => !isDisabled && handleView(dashboard.id)}>
                            {dashboard.name.length > 20
                              ? dashboard.name.substring(0, 22) + '...'
                              : dashboard.name}
                          </span>
                        </OverlayTrigger>

                        <div className="d-flex gap-2">
                          {/* View button (allowed even if disabled) */}
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id={`tooltip-view-${dashboard.id}`}>View Dashboard</Tooltip>
                            }>
                            <span className="d-inline-block">
                              <FaEye
                                className={`cursor-pointer ${isDisabled ? 'text-muted' : ''}`}
                                onClick={() => !isDisabled && handleView(dashboard.id)}
                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                              />
                            </span>
                          </OverlayTrigger>

                          {/* Edit button (disabled if dashboard.is_disabled) */}
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id={`tooltip-edit-${dashboard.id}`}>Edit Dashboard</Tooltip>
                            }>
                            <span className="d-inline-block">
                              <MdEdit
                                className={`cursor-pointer ${isDisabled ? 'text-muted' : ''}`}
                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                onClick={() => !isDisabled && handleEdit(dashboard.id)}
                              />
                            </span>
                          </OverlayTrigger>

                          {/* Delete button (disabled if dashboard.is_disabled) */}
                          <OverlayTrigger
                            placement="bottom"
                            overlay={
                              <Tooltip id={`tooltip-delete-${dashboard.id}`}>
                                Delete Dashboard
                              </Tooltip>
                            }>
                            <span className="d-inline-block">
                              <FaTrash
                                className={`cursor-pointer ${isDisabled ? 'text-muted' : ''}`}
                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                onClick={() => !isDisabled && confirmDelete(dashboard.id)}
                              />
                            </span>
                          </OverlayTrigger>
                        </div>
                      </div>
                      {index !== dashboards.length - 1 && <hr className="my-3" />}
                    </div>
                  );
                })
              ) : (
                <p className="text-center py-3">No dashboards found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <div ref={modalRef}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this dashboard?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              className="btn-grad"
              onClick={performDelete}
              disabled={deleteLoading}>
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}
